import React, { useState } from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';

import { formatOptimizationCost, formatShareOfDesignTotal } from './formatOptimizationCost';
import {
  GroupedOptimizationCostBreakdown,
  OptimizationCostGroup,
  OptimizationCostLineItem,
} from './optimizationCostBreakdown';

interface Props {
  readonly title: string;
  readonly breakdown: GroupedOptimizationCostBreakdown;
}

export function CostComparisonCard({ title, breakdown }: Props) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion expanded={expanded} onChange={(_event, isExpanded) => setExpanded(isExpanded)} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1, pr: 1 }}
        >
          <Typography>{title}</Typography>
          <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatOptimizationCost(breakdown.total)}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {breakdown.groups.map((group) => (
            <CostGroupSection key={group.key} group={group} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function CostGroupSection({ group }: { readonly group: OptimizationCostGroup }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_event, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      sx={{ bgcolor: 'action.hover', boxShadow: 'none', '&::before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1, pr: 1 }}
        >
          <RowLabel label={group.label} shareOfDesignTotal={group.shareOfDesignTotal} />
          <RowFigures amount={group.subtotal} deltaFromPrevious={group.deltaFromPrevious} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto 1fr',
            alignItems: 'center',
            columnGap: 1,
            rowGap: 0.5,
          }}
        >
          {group.lineItems.map((lineItem) => (
            <LineItemRow key={lineItem.term} lineItem={lineItem} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function LineItemRow({ lineItem }: { readonly lineItem: OptimizationCostLineItem }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
        {lineItem.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {lineItem.shareOfDesignTotal == null ? '' : formatShareOfDesignTotal(lineItem.shareOfDesignTotal)}
      </Typography>
      <RowFigures amount={lineItem.amount} deltaFromPrevious={lineItem.deltaFromPrevious} muted alignEnd />
    </>
  );
}

interface RowLabelProps {
  readonly label: string;
  readonly shareOfDesignTotal?: number;
}

function RowLabel({ label, shareOfDesignTotal }: RowLabelProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography variant="body2">{label}</Typography>
      {shareOfDesignTotal != null && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatShareOfDesignTotal(shareOfDesignTotal)}
        </Typography>
      )}
    </Box>
  );
}

interface RowFiguresProps {
  readonly amount: number;
  readonly deltaFromPrevious?: number;
  readonly muted?: boolean;
  readonly alignEnd?: boolean;
}

function RowFigures({ amount, deltaFromPrevious, muted, alignEnd }: RowFiguresProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        justifySelf: alignEnd ? 'end' : undefined,
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 0.25,
      }}
    >
      <Typography
        variant="body2"
        color={muted ? 'text.secondary' : undefined}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatOptimizationCost(amount)}
      </Typography>
      {deltaFromPrevious != null && <CostChange change={deltaFromPrevious} />}
    </Box>
  );
}

function CostChange({ change }: { readonly change: number }) {
  const cents = Math.round(change * 100);
  const color = cents < 0 ? 'success.main' : cents > 0 ? 'warning.main' : 'text.secondary';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.25,
        color,
        whiteSpace: 'nowrap',
        minWidth: '5em',
      }}
    >
      {cents < 0 && <ArrowDownwardIcon sx={{ fontSize: '0.9rem' }} />}
      {cents > 0 && <ArrowUpwardIcon sx={{ fontSize: '0.9rem' }} />}
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatOptimizationCost(Math.abs(cents) / 100)}
      </Typography>
    </Box>
  );
}
